import appConfig from '@frontend/env';
import { createStore } from '@frontend/store';

type AppConfigKey = keyof typeof appConfig;

interface ConfigProperty<Key extends AppConfigKey = AppConfigKey> {
  key: Key;
  default?: string;
  alertIfDefaultUsed?: boolean;

  /**
   * Will throw error unless set to `false`
   */
  required?: boolean;
}

type ToObject<CP extends ConfigProperty[]> = {
  [K in CP[number]['key']]: string | undefined;
};

type Prettify<T> = {
  [K in keyof T]: T[K];
} & NonNullable<unknown>;

function compileConfigState<CP extends ConfigProperty[]>(configProperties: CP): Prettify<ToObject<CP>> {
  return configProperties.reduce((acc, cp) => {
    return {
      ...acc,
      [cp.key]: appConfig[cp.key],
    };
  }, {} as Record<CP[number]['key'], string | undefined>);
}

export type MakeAppConfigStoreProps<CP extends ConfigProperty[]> = {
  config: CP;
  uniqueStoreName: string;
  handleMissingValue: (key: AppConfigKey, value: string | undefined) => void;
  handleDefaultUsed: (key: AppConfigKey, value: string | undefined) => void;
};

export function makeAppConfigStore<CP extends ConfigProperty[]>({
  uniqueStoreName,
  config,
  handleMissingValue,
  handleDefaultUsed,
}: MakeAppConfigStoreProps<CP>) {
  const compiledConfig = compileConfigState(config);

  const queue: CallableFunction[] = [];

  for (const cp of config) {
    const value = compiledConfig[cp.key as keyof typeof compiledConfig];
    if ((value === '' || value === undefined) && cp.required !== false) {
      queue.push(handleMissingValue.bind(null, cp.key, value));
    }
    if (!value && !!cp.default) {
      compiledConfig[cp.key as keyof typeof compiledConfig] = cp.default;
      if (cp.alertIfDefaultUsed) {
        queue.push(handleDefaultUsed.bind(null, cp.key, value));
      }
    }
  }

  return createStore(
    () => {
      return {
        ...compiledConfig,
        onSentryLoaded: () => {
          while (queue.length) {
            const fn = queue.shift();
            if (fn) fn();
          }
        },
      };
    },
    {
      name: uniqueStoreName,
    }
  );
}
